import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// ADDED (2026-09-18): self-service "delete my own passkeys" — accountUpdate.js's
// new GET/DELETE /account/webauthn-devices routes. Before this, only an
// Authentik admin could remove a passkey (via its own admin panel), which
// doesn't scale once someone's accumulated a few duplicates (see this
// session's own investigation into why Authentik's own enrollment stage
// never warns about re-enrolling the same passkey-manager-backed
// credential — no excludeCredentials support). Reachable from TopBanner.jsx's
// Profile panel once webauthnStatus.enrolled is true.

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4001/api';

const page = {
  minHeight: '100vh',
  boxSizing: 'border-box',
  backgroundImage: 'url(/BESCOM_SSO_BG_Image.png)',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
};

const card = {
  background: 'rgba(255, 255, 255, 0.65)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  border: '1px solid rgba(255, 255, 255, 0.5)',
  borderRadius: '16px',
  padding: '48px 40px',
  width: '100%',
  maxWidth: '480px',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.35)',
};

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch (e) {
    return iso;
  }
}

export default function ManagePasskeys() {
  const navigate = useNavigate();
  const [devices, setDevices] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  // Tracks which device pk is mid-delete, so only THAT row shows a busy
  // state — not a page-wide spinner, which would be jarring for a list
  // action.
  const [deletingPk, setDeletingPk] = useState(null);
  // Two-step confirm (click once to arm, again to actually delete) instead
  // of a native window.confirm() — matches this app's general preference
  // for inline UI over browser-native dialogs, and deletion here is
  // permanent (no undo, no admin-side "restore").
  const [confirmingPk, setConfirmingPk] = useState(null);

  useEffect(() => {
    fetchDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchDevices() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/account/webauthn-devices`, {
        credentials: 'include',
      });
      if (res.status === 401) {
        navigate('/', { state: { sessionExpired: true } });
        return;
      }
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Could not load your passkeys.');
        setDevices([]);
        return;
      }
      setDevices(data.devices || []);
    } catch (e) {
      setError('Could not reach the account service.');
      setDevices([]);
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(pk) {
    setDeletingPk(pk);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/account/webauthn-devices/${encodeURIComponent(pk)}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.status === 401) {
        navigate('/', { state: { sessionExpired: true } });
        return;
      }
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Could not delete this passkey. Please try again.');
        setDeletingPk(null);
        setConfirmingPk(null);
        return;
      }
      setDevices((prev) => (prev || []).filter((d) => d.pk !== pk));
      setConfirmingPk(null);
      setDeletingPk(null);
    } catch (e) {
      setError('Could not reach the account service.');
      setDeletingPk(null);
    }
  }

  function handleBack() {
    navigate('/dashboard');
  }

  return (
    <div style={page}>
      <div style={card}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <img src="/bescom-logo-ui.png" alt="BESCOM" style={{ width: '80px', marginBottom: '16px' }} />
          <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#1a1a1a', margin: '0 0 8px' }}>
            Manage your Passkeys
          </h1>
          <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>
            Remove any passkey you no longer use or don't recognize
          </p>
        </div>

        {error && (
          <div style={{
            background: '#FDECEC', color: '#B3261E', padding: '10px 14px',
            borderRadius: '8px', fontSize: '13px', marginBottom: '16px',
          }}>
            {error}
          </div>
        )}

        {loading ? (
          <p style={{ textAlign: 'center', color: '#666', fontSize: '14px' }}>Loading…</p>
        ) : !devices || devices.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#666', fontSize: '14px' }}>
            You don't have any passkeys set up yet.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '8px' }}>
            {devices.map((d) => (
              <div
                key={d.pk}
                style={{
                  background: '#F0F4F8', border: '1px solid #D3E3ED', borderRadius: '10px',
                  padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#1a1a1a' }}>
                    🔑 {d.deviceType || d.name || 'Passkey'}
                  </p>
                  <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#888' }}>
                    Added {formatDate(d.createdOn)}
                  </p>
                </div>
                {confirmingPk === d.pk ? (
                  <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                    <button
                      type="button"
                      onClick={() => handleDelete(d.pk)}
                      disabled={deletingPk === d.pk}
                      style={{
                        background: '#B3261E', color: '#fff', border: 'none',
                        borderRadius: '8px', padding: '8px 12px', fontSize: '13px', fontWeight: '600',
                        cursor: deletingPk === d.pk ? 'not-allowed' : 'pointer',
                        opacity: deletingPk === d.pk ? 0.6 : 1,
                      }}
                    >
                      {deletingPk === d.pk ? 'Deleting…' : 'Confirm'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmingPk(null)}
                      disabled={deletingPk === d.pk}
                      style={{
                        background: 'none', color: '#666', border: '1px solid #D3D1C7',
                        borderRadius: '8px', padding: '8px 12px', fontSize: '13px', fontWeight: '500',
                        cursor: deletingPk === d.pk ? 'not-allowed' : 'pointer',
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmingPk(d.pk)}
                    style={{
                      background: 'none', color: '#B3261E', border: '1px solid #F0C6C2',
                      borderRadius: '8px', padding: '8px 12px', fontSize: '13px', fontWeight: '600',
                      cursor: 'pointer', flexShrink: 0,
                    }}
                  >
                    Delete
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        <button
          onClick={handleBack}
          style={{
            width: '100%', marginTop: '16px', padding: '12px',
            background: 'none', border: '1px solid #D3D1C7', color: '#666',
            borderRadius: '10px', fontSize: '14px', fontWeight: '500', cursor: 'pointer',
          }}
        >
          Back to Dashboard
        </button>
      </div>
    </div>
  );
}
